import { useMemo, useState } from "react";
import type { ColumnDef } from "@tanstack/react-table";

import { api } from "../api/client";
import type { PhoneNumber, Tenant, Trunk, VoiceApp } from "../api/types";
import { failureMessage, useResource } from "../api/useResource";
import { usePagedResource } from "../api/usePagedResource";
import { DataTable } from "../components/data-table";
import type { DataTableRowAction } from "../components/data-table";
import {
  ActionError, Badge, Button, Field, Modal, PageHeader, inputClass,
} from "../components/ui";
import { useSession } from "../store/session";

// Números (DID): los que reciben llamadas entrantes.
//
// Hay dos operaciones con dueños distintos y la pantalla lo refleja:
//
//   - **Alta** del número en la plataforma: es de OMITECH, porque hay que
//     decir por qué troncal entra. Exige superadmin (`platformWrite` +
//     `trunks.manage` en internal/api/router.go:168).
//   - **Ruta entrante**: es del tenant (`numbers.manage`). Es la parte que el
//     cliente cambia a diario.
//
// Un número sin ruta no es un error: está asignado y todavía sin configurar.
// Las llamadas que lleguen a él no tienen a quién preguntar qué hacer, así que
// la vista lo marca.

export function Numbers() {
  const numbers = usePagedResource<PhoneNumber>("/api/v1/numbers");
  const voiceApps = useResource<VoiceApp[]>("/api/v1/voice-apps");

  const can = useSession((s) => s.can);
  const isSuperadmin = useSession((s) => s.session?.user.is_superadmin ?? false);
  const sinCuentaActiva = useSession((s) => (s.session?.active_tenant_id ?? "") === "");

  // El reparto del inventario solo tiene sentido en la vista de plataforma:
  // ahí es donde se ven los números sin dueño y los de todas las cuentas.
  const vistaDeInventario = isSuperadmin && sinCuentaActiva;
  const cuentas = useResource<Tenant[]>(vistaDeInventario ? "/api/v1/tenants" : null);
  const [asignando, setAsignando] = useState<string | null>(null);

  async function asignar(numberId: string, tenantId: string | null) {
    setActionError(null);
    setAsignando(numberId);
    try {
      await api.put(`/api/v1/numbers/${numberId}/assignment`, { tenant_id: tenantId });
      await numbers.reload(true);
    } catch (err) {
      setActionError(failureMessage(err, "no se pudo cambiar la asignación"));
    } finally {
      setAsignando(null);
    }
  }

  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<PhoneNumber | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const columnas = useMemo<ColumnDef<PhoneNumber, any>[]>(() => {
    const base: ColumnDef<PhoneNumber, any>[] = [
      {
        id: "e164",
        accessorKey: "e164",
        header: "Número",
        meta: { label: "Número" },
        cell: ({ row }) => (
          <>
            <div className="font-mono text-slate-100">{row.original.e164}</div>
            <div className="font-mono text-xs text-slate-500">{row.original.id}</div>
          </>
        ),
      },
      {
        id: "country",
        accessorKey: "country",
        header: "País",
        meta: { label: "País", width: "5rem" },
        cell: ({ row }) => <span className="text-slate-600">{row.original.country}</span>,
      },
      {
        id: "number_type",
        accessorKey: "number_type",
        header: "Tipo",
        meta: { label: "Tipo", hideOnMobile: true },
        cell: ({ row }) => (
          <span className="text-slate-600">
            {NUMBER_TYPE_LABELS[row.original.number_type] ?? row.original.number_type}
          </span>
        ),
      },
      {
        id: "status",
        accessorKey: "status",
        header: "Estado",
        meta: { label: "Estado", width: "8rem" },
        cell: ({ row }) => (
          <Badge tone={NUMBER_STATUS_TONES[row.original.status] ?? "neutral"}>
            {NUMBER_STATUS_LABELS[row.original.status] ?? row.original.status}
          </Badge>
        ),
      },
    ];

    // La columna de reparto solo existe en la vista de plataforma: en la de un
    // tenant no hay nada que repartir y sería una columna muerta.
    if (vistaDeInventario) {
      base.push({
        id: "tenant_id",
        accessorKey: "tenant_id",
        header: "Cuenta",
        enableSorting: false,
        meta: { label: "Cuenta" },
        cell: ({ row }) => (
          <AsignacionDeCuenta
            number={row.original}
            cuentas={cuentas.data}
            error={cuentas.error}
            disabled={asignando === row.original.id}
            onChange={(tenantId) => void asignar(row.original.id, tenantId)}
          />
        ),
      });
    }

    base.push({
      id: "ruta",
      accessorFn: (number) => number.voice_app_id ?? number.voice_url ?? "",
      header: "Ruta entrante",
      enableSorting: false,
      meta: { label: "Ruta entrante" },
      cell: ({ row }) => <Route number={row.original} voiceApps={voiceApps.data ?? []} />,
    });

    return base;
  }, [vistaDeInventario, cuentas.data, cuentas.error, asignando, voiceApps.data]);

  const acciones = useMemo<DataTableRowAction<PhoneNumber>[]>(
    () =>
      can("numbers.manage")
        ? [
            {
              label: "Configurar ruta",
              onSelect: (number) => {
                setActionError(null);
                setEditing(number);
              },
            },
          ]
        : [],
    [can],
  );

  return (
    <div>
      <PageHeader
        title="Números"
        description="Los DID que reciben llamadas entrantes. La ruta decide a quién le pregunta la plataforma qué hacer cuando entra una llamada."
        action={
          isSuperadmin && !creating && <Button onClick={() => setCreating(true)}>Dar de alta un número</Button>
        }
      />

      {actionError !== null && <ActionError message={actionError} />}

      {creating && (
        <CreateNumberForm
          onCancel={() => setCreating(false)}
          onCreated={() => {
            setCreating(false);
            void numbers.reload(true);
          }}
        />
      )}

      {editing !== null && (
        <RouteForm
          number={editing}
          voiceApps={voiceApps.data ?? []}
          voiceAppsError={voiceApps.error}
          onCancel={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            void numbers.reload(true);
          }}
        />
      )}

      <DataTable<PhoneNumber>
        data={numbers.data ?? []}
        columns={columnas}
        getRowId={(number) => number.id}
        loading={numbers.loading}
        error={numbers.error}
        onRetry={() => void numbers.reload(false)}
        caption="Números asignados"
        empty={{
          title: "Sin números asignados",
          description:
            "Las llamadas entrantes necesitan un DID asignado a esta cuenta. El alta la hace OMITECH, porque depende de la troncal por la que entra el número.",
        }}
        search={{ placeholder: "Buscar por número…" }}
        filters={[
          {
            columnId: "status",
            label: "Estado",
            options: [
              { value: "assigned", label: "Asignado" },
              { value: "available", label: "En inventario" },
              { value: "suspended", label: "Suspendido" },
            ],
          },
          {
            columnId: "number_type",
            label: "Tipo",
            options: [
              { value: "local", label: "Local" },
              { value: "mobile", label: "Móvil" },
              { value: "tollfree", label: "Sin costo" },
            ],
          },
        ]}
        rowActions={acciones}
        hideableColumns
        urlStateKey="numeros"
        pagination={{
          pageSize: numbers.enEstaPagina,
          hasNext: numbers.hayMas,
          hasPrevious: numbers.hayAnterior,
          onNext: numbers.siguiente,
          onPrevious: numbers.anterior,
        }}
      />
    </div>
  );
}

const NUMBER_TYPE_LABELS: Record<string, string> = {
  local: "Local",
  mobile: "Móvil",
  tollfree: "Sin costo para quien llama",
};

// Los estados reales del modelo (migrations/00003, CHECK de phone_numbers).
// `assigned` es el estado normal de un número en uso, no un aviso: un número
// `available` es el que está en el inventario sin dueño.
const NUMBER_STATUS_LABELS: Record<string, string> = {
  available: "En inventario",
  assigned: "Asignado",
  suspended: "Suspendido",
};

const NUMBER_STATUS_TONES: Record<string, "neutral" | "good" | "warn" | "bad"> = {
  available: "neutral",
  assigned: "good",
  suspended: "warn",
};

/** Route resume a dónde van las llamadas entrantes de un número. */
function Route({ number, voiceApps }: { number: PhoneNumber; voiceApps: VoiceApp[] }) {
  if (number.voice_app_id !== null) {
    const app = voiceApps.find((a) => a.id === number.voice_app_id);
    return (
      <div>
        <div className="text-slate-100">{app?.name ?? "Aplicación de voz"}</div>
        <div className="font-mono text-xs text-slate-500">
          {app?.source === "markup" ? "markup guardado · sin petición HTTP" : number.voice_app_id}
        </div>
      </div>
    );
  }
  if (number.voice_url !== null) {
    return (
      <div>
        <div className="truncate font-mono text-xs text-slate-300">{number.voice_url}</div>
        {number.fallback_url !== null && number.fallback_url !== undefined && (
          <div className="truncate font-mono text-xs text-slate-500">
            respaldo: {number.fallback_url}
          </div>
        )}
      </div>
    );
  }
  return (
    <span className="text-sm text-amber-300">
      Sin configurar: las llamadas entrantes no tienen instrucciones
    </span>
  );
}

function RouteForm({
  number,
  voiceApps,
  voiceAppsError,
  onCancel,
  onSaved,
}: {
  number: PhoneNumber;
  voiceApps: VoiceApp[];
  voiceAppsError: string | null;
  onCancel: () => void;
  onSaved: () => void;
}) {
  // El backend exige exactamente una de las dos (internal/numbers/handlers.go:
  // "voice_app_id and voice_url are mutually exclusive"), así que la UI es un
  // selector y no dos campos que se puedan llenar a la vez.
  //
  // Ojo con la diferencia, que no es la que parece: **las dos consultan una
  // URL**. Una aplicación de voz no guarda markup, guarda la configuración de
  // la Voice URL (ver internal/calls/voiceflow.go, `startURL`: la de la app es
  // el último recurso de la misma cadena). Lo que cambia es dónde vive esa
  // configuración, no cómo se ejecuta.
  const [mode, setMode] = useState<"app" | "url">(number.voice_url !== null ? "url" : "app");
  const [voiceAppId, setVoiceAppId] = useState(number.voice_app_id ?? "");
  const [voiceUrl, setVoiceUrl] = useState(number.voice_url ?? "");
  const [fallbackUrl, setFallbackUrl] = useState(number.fallback_url ?? "");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const body =
        mode === "app"
          ? { voice_app_id: voiceAppId, voice_url: null }
          : {
              voice_app_id: null,
              voice_url: voiceUrl,
              fallback_url: fallbackUrl === "" ? null : fallbackUrl,
            };
      await api.patch(`/api/v1/numbers/${number.id}`, body);
      onSaved();
    } catch (err) {
      setError(failureMessage(err, "no se pudo guardar la ruta"));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      title={`Ruta entrante de ${number.e164}`}
      description="A dónde van las llamadas que entren por este número."
      width="lg"
      onClose={onCancel}
    >

      <form className="space-y-4" onSubmit={onSubmit}>
        <fieldset>
          <legend className="text-sm font-medium text-slate-300">Qué contesta</legend>
          <div className="mt-2 space-y-1">
            <label className="flex items-center gap-2 text-sm text-slate-300">
              <input
                type="radio"
                checked={mode === "app"}
                onChange={() => setMode("app")}
              />
              Una aplicación de voz
            </label>
            <label className="flex items-center gap-2 text-sm text-slate-300">
              <input
                type="radio"
                checked={mode === "url"}
                onChange={() => setMode("url")}
              />
              Una Voice URL escrita aquí, solo para este número
            </label>
          </div>
        </fieldset>

        {mode === "app" ? (
          <Field
            label="Aplicación de voz"
            hint="Una aplicación puede guardar el markup —se ejecuta sin salir a la red— o apuntar a una Voice URL tuya. En los dos casos es configuración con nombre, reutilizable entre números."
          >
            {voiceAppsError !== null ? (
              <p className="text-sm text-rose-300">
                No se pudieron cargar las aplicaciones de voz: {voiceAppsError}
              </p>
            ) : voiceApps.length === 0 ? (
              <p className="text-sm text-amber-300">
                No hay aplicaciones de voz en esta cuenta. Crea una en «Aplicaciones de voz» o usa
                una Voice URL.
              </p>
            ) : (
              <select
                className={inputClass}
                required
                value={voiceAppId}
                onChange={(e) => setVoiceAppId(e.target.value)}
              >
                <option value="">Elige una…</option>
                {voiceApps.map((app) => (
                  <option key={app.id} value={app.id}>
                    {app.name}
                  </option>
                ))}
              </select>
            )}
          </Field>
        ) : (
          <>
            <Field
              label="Voice URL"
              hint="Debe ser https y resolver a una dirección pública. Se valida al guardar y en cada llamada."
            >
              <input
                className={inputClass}
                type="url"
                required
                placeholder="https://cliente.example.com/voice"
                value={voiceUrl}
                onChange={(e) => setVoiceUrl(e.target.value)}
              />
            </Field>
            <Field
              label="URL de respaldo"
              hint="Opcional. Se consulta si la principal falla o tarda demasiado; sin ella, la llamada se cuelga."
            >
              <input
                className={inputClass}
                type="url"
                placeholder="https://respaldo.example.com/voice"
                value={fallbackUrl}
                onChange={(e) => setFallbackUrl(e.target.value)}
              />
            </Field>
          </>
        )}

        {error !== null && <p className="text-sm text-rose-300">{error}</p>}

        <div className="flex gap-2">
          <Button type="submit" disabled={submitting}>
            {submitting ? "Guardando…" : "Guardar ruta"}
          </Button>
          <Button type="button" variant="secondary" onClick={onCancel}>
            Cancelar
          </Button>
        </div>
      </form>
    </Modal>
  );
}

/**
 * CreateNumberForm da de alta un DID en la plataforma.
 *
 * Solo superadmin: hace falta elegir la troncal por la que entra el número, y
 * las troncales son de la plataforma, no del tenant.
 */
function CreateNumberForm({ onCancel, onCreated }: { onCancel: () => void; onCreated: () => void }) {
  const trunks = useResource<Trunk[]>("/api/v1/trunks");
  const activeTenantId = useSession((s) => s.session?.active_tenant_id ?? "");

  const [e164, setE164] = useState("");
  const [country, setCountry] = useState("MX");
  const [trunkId, setTrunkId] = useState("");
  const [assign, setAssign] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await api.post("/api/v1/numbers", {
        e164,
        trunk_id: trunkId,
        country,
        tenant_id: assign ? activeTenantId : null,
      });
      onCreated();
    } catch (err) {
      setError(failureMessage(err, "no se pudo dar de alta el número"));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      title="Añadir un número"
      description="Un número sin ruta no contesta: puedes asignarla después."
      onClose={onCancel}
    >
      <form className="space-y-4" onSubmit={onSubmit}>
        <Field label="Número en formato E.164" hint="Con signo de más y código de país.">
          <input
            className={inputClass}
            required
            inputMode="tel"
            placeholder="+528112345678"
            value={e164}
            onChange={(e) => setE164(e.target.value.trim())}
          />
        </Field>

        <Field label="País" hint="Código ISO de dos letras en mayúsculas.">
          <input
            className={`${inputClass} max-w-[8rem]`}
            required
            pattern="[A-Z]{2}"
            maxLength={2}
            value={country}
            onChange={(e) => setCountry(e.target.value.toUpperCase())}
          />
        </Field>

        <Field
          label="Troncal de entrada"
          hint="Por dónde llegan las llamadas a este número. Sin la troncal correcta, el DID no enruta."
        >
          {trunks.error !== null ? (
            <p className="text-sm text-rose-300">No se pudieron cargar las troncales: {trunks.error}</p>
          ) : trunks.loading ? (
            <p className="text-sm text-slate-500">Cargando troncales…</p>
          ) : (trunks.data ?? []).length === 0 ? (
            <p className="text-sm text-amber-300">
              No hay troncales registradas. Crea una en «Troncales» antes de dar de alta números.
            </p>
          ) : (
            <select
              className={inputClass}
              required
              value={trunkId}
              onChange={(e) => setTrunkId(e.target.value)}
            >
              <option value="">Elige una…</option>
              {(trunks.data ?? [])
                .filter((t) => t.direction !== "outbound")
                .map((trunk) => (
                  <option key={trunk.id} value={trunk.id}>
                    {trunk.name} ({trunk.endpoint_name})
                  </option>
                ))}
            </select>
          )}
        </Field>

        <label className="flex items-start gap-2 text-sm text-slate-300">
          <input
            type="checkbox"
            className="mt-1"
            checked={assign}
            onChange={(e) => setAssign(e.target.checked)}
          />
          <span>
            Asignarlo a la cuenta activa
            <span className="block text-xs text-slate-500">
              Sin marcar, el número queda en el inventario de la plataforma, sin dueño.
            </span>
          </span>
        </label>

        {error !== null && <p className="text-sm text-rose-300">{error}</p>}

        <div className="flex gap-2">
          <Button type="submit" disabled={submitting}>
            {submitting ? "Dando de alta…" : "Dar de alta"}
          </Button>
          <Button type="button" variant="secondary" onClick={onCancel}>
            Cancelar
          </Button>
        </div>
      </form>
    </Modal>
  );
}

/**
 * AsignacionDeCuenta reparte un número del inventario.
 *
 * Cambiar de dueño **borra la ruta entrante**, porque apunta al servidor del
 * cliente anterior. Se avisa antes de hacerlo y no después: recuperar la ruta
 * es rehacerla a mano, y mientras tanto las llamadas a ese número no contestan.
 */
function AsignacionDeCuenta({
  number,
  cuentas,
  error,
  disabled,
  onChange,
}: {
  number: PhoneNumber;
  cuentas: Tenant[] | null;
  error: string | null;
  disabled: boolean;
  onChange: (tenantId: string | null) => void;
}) {
  if (error !== null) {
    return <span className="text-xs text-rose-300">Cuentas no disponibles</span>;
  }
  if (cuentas === null) return <span className="text-xs text-slate-600">Cargando…</span>;

  const actual = number.tenant_id ?? "";

  return (
    <select
      className="max-w-[12rem] rounded-lg border border-white/15 bg-white/[0.04] px-2.5 py-1 text-xs text-slate-200 transition focus:border-sky-400/60 focus:outline-none [&>option]:bg-[#11162a]"
      disabled={disabled}
      value={actual}
      onChange={(e) => {
        const destino = e.target.value === "" ? null : e.target.value;
        if (destino === actual || (destino === null && actual === "")) return;
        const nombre =
          destino === null
            ? "el inventario (sin dueño)"
            : (cuentas.find((c) => c.id === destino)?.name ?? "otra cuenta");
        // El `confirm` del navegador basta aquí: es una fila de tabla, y un
        // modal por fila sería más ceremonia que la acción.
        const seguir = window.confirm(
          `Mover ${number.e164} a ${nombre}.\n\n` +
            "Su ruta entrante se borra: apunta al servidor del dueño actual, y " +
            "conservarla mandaría las llamadas a quien ya no es su cliente.",
        );
        if (seguir) onChange(destino);
        else e.target.value = actual;
      }}
    >
      <option value="">— inventario —</option>
      {cuentas.map((cuenta) => (
        <option key={cuenta.id} value={cuenta.id}>
          {cuenta.name}
        </option>
      ))}
    </select>
  );
}
